import { forwardRef, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { List as ImmutableList, Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import styled from 'foundations-theming/utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
// @ts-expect-error module not typed
import { getInReplyToId } from 'conversations-message-history/common-message-format/operators/commonMessageFormatGetters';
import { getCommonMessage } from 'conversations-thread-data/common-reply/public/operators/commonReplyGetters';
import { getForwardCommunicatorState } from 'composer-data/email/public/selectors';
// TODO: move to composer-ui
import { getFocusedThreadVisitor } from 'conversations-thread-view/visitor/selectors/getFocusedThreadVisitor';
import { getRecipientsIconName } from '../operators/getRecipientsIconName';
import DeliveryIdentifierSearchInput from './DeliveryIdentifierSearchInput';
import MetadataInputLabel from './MetadataInputLabel';
import { RecipientTooltipTitle } from './RecipientTooltipTitle';
import GenericRecipientSearchInput from './GenericRecipientSearchInput';
import { getAuthAsPojo } from 'conversations-thread-data/auth/public/selectors';
import { useNeverLog } from '../../../email/protected/hooks';
import { getSenders } from 'conversations-message-history/common-message-format/operators/commonMessageGetters';
import { getDeliveryIdentifier } from 'conversations-message-history/common-message-format/operators/senderGetters';
import { getDeliveryIdentifierValue } from 'conversations-message-history/common-message-format/operators/deliveryIdentifierGetters';
import { useChannelInstances } from 'conversations-thread-data/channel-instances/public/hooks';
import { getData } from 'conversations-async-data/async-data/operators/getters';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FieldWrapper = styled.label.withConfig({
  displayName: "RecipientField__FieldWrapper",
  componentId: "sc-18qwvgk-0"
})(["display:inline-flex;flex-grow:1;width:90%;"]);
const noop = () => {};
function RecipientFieldLabel({
  fieldName
}) {
  switch (fieldName) {
    case 'CC':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "composer-ui.metadata-editor.recipient-field.label.cc"
      });
    case 'BCC':
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "composer-ui.metadata-editor.recipient-field.label.bcc"
      });
    case 'TO':
    default:
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "composer-ui.metadata-editor.recipient-field.label.to"
      });
  }
}
RecipientFieldLabel.displayName = 'RecipientFieldLabel';
const _RecipientField = (props, ref) => {
  var _getSenders, _getData$map$filter, _getData;
  const {
    allRecipients = ImmutableMap(),
    delayedExpanded = true,
    deliveryIdentifier,
    fieldName,
    handleSetExpanded = noop,
    inboxId,
    index,
    isComposer,
    recipientFieldsExpansionState,
    recipientKey,
    reply,
    subfieldAutofocus = false,
    valueChangeHandler = noop,
    toLabel,
    useWhatsAppCompose
  } = props;
  const {
    replyAll
  } = useSelector(getForwardCommunicatorState);
  const defaultRecipient = useSelector(getFocusedThreadVisitor);
  const auth = useSelector(getAuthAsPojo);
  const scopes = auth && auth.user ? auth.user.scopes : [];
  const commonMessageReply = getCommonMessage(reply);
  const inReplyToId = getInReplyToId(commonMessageReply);
  const iconName = getRecipientsIconName({
    allRecipients,
    commonMessageReply,
    replyAll
  });
  const {
    emailBlocklistStatus
  } = useNeverLog({
    commonMessage: commonMessageReply
  });
  const value = recipientKey ? allRecipients.get(recipientKey) : ImmutableList();
  const [relatedRecipients] = useState(() => value);
  const sender = (_getSenders = getSenders(commonMessageReply)) === null || _getSenders === void 0 ? void 0 : _getSenders.first();
  const senderDeliveryIdentifier = sender && getDeliveryIdentifier(sender);
  const senderDeliveryIdentifierValue = senderDeliveryIdentifier && getDeliveryIdentifierValue(senderDeliveryIdentifier);
  const {
    asyncChannelInstances
  } = useChannelInstances({
    inboxId
  });
  const channelInstanceIds = ImmutableSet((_getData$map$filter = (_getData = getData(asyncChannelInstances)) === null || _getData === void 0 || (_getData = _getData.map(instance => instance.isActive ? instance.channelId : null)) === null || _getData === void 0 ? void 0 : _getData.filter(Boolean)) !== null && _getData$map$filter !== void 0 ? _getData$map$filter : []);
  const filterRecipients = useCallback(recipient => {
    const matchingChannelInstanceForRecipient = channelInstanceIds === null || channelInstanceIds === void 0 ? void 0 : channelInstanceIds.includes(recipient.genericChannelId);
    return Boolean(matchingChannelInstanceForRecipient);
  }, [channelInstanceIds]);
  const hasWhatsAppAccess = scopes.includes('messaging-access') && scopes.includes('messaging-trusted');
  const searchInput = useWhatsAppCompose ? /*#__PURE__*/_jsx(GenericRecipientSearchInput, {
    contactProperty: hasWhatsAppAccess ? deliveryIdentifier : 'email',
    autoFocus: index === 0 ? true : subfieldAutofocus,
    clearableValues: true,
    value: value,
    onFocus: index === 0 ? handleSetExpanded : noop,
    onChange: valueChangeHandler,
    defaultRecipient: index === 0 && !isComposer ? defaultRecipient : null,
    relatedRecipients: relatedRecipients,
    emailBlocklistStatus: emailBlocklistStatus,
    senderDeliveryIdentifierValue: senderDeliveryIdentifierValue,
    filter: filterRecipients,
    index: index,
    ref: ref
  }) : /*#__PURE__*/_jsx(DeliveryIdentifierSearchInput, {
    deliveryIdentifier: deliveryIdentifier,
    autoFocus: index === 0 ? true : subfieldAutofocus,
    clearableValues: true,
    value: value,
    onFocus: index === 0 ? handleSetExpanded : noop,
    onChange: valueChangeHandler,
    defaultRecipient: index === 0 && !isComposer ? defaultRecipient : null,
    relatedRecipients: relatedRecipients,
    emailBlocklistStatus: emailBlocklistStatus,
    index: index,
    ref: ref
  });
  const label = index === 0 ? toLabel : /*#__PURE__*/_jsx(RecipientFieldLabel, {
    fieldName: fieldName
  });
  if (inReplyToId) {
    return index === 0 ? /*#__PURE__*/_jsxs(FieldWrapper, {
      "data-selenium-test": `metadata-${recipientKey}-input`,
      children: [/*#__PURE__*/_jsx(Tooltip, {
        title: /*#__PURE__*/_jsx(RecipientTooltipTitle, {
          iconName: iconName.name
        }),
        children: /*#__PURE__*/_jsx(IconHolder, {
          "data-selenium-test": `metadata-${recipientKey}-icon`,
          icon: iconName.icon,
          display: "inline-flex",
          alignItems: "center",
          padding: "0",
          marginInlineEnd: "200",
          size: "600"
        })
      }), searchInput]
    }) : /*#__PURE__*/_jsx(MetadataInputLabel, {
      label: label,
      "data-selenium-test": `metadata-${recipientKey}-input`,
      children: searchInput
    });
  }
  const shouldDisplaySearchInput = delayedExpanded && (recipientFieldsExpansionState.get(index) || index === 0);
  if (!shouldDisplaySearchInput) {
    return null;
  }
  return /*#__PURE__*/_jsx(MetadataInputLabel, {
    label: label,
    "data-selenium-test": `metadata-${recipientKey}-input`,
    children: searchInput
  });
};
_RecipientField.displayName = '_RecipientField';
const RecipientField = /*#__PURE__*/forwardRef(_RecipientField);
export default RecipientField;